// Looping UI scenes written as a story: every part of a scene plays one
// keyframe track over the whole loop, its stops are milliseconds of the loop.
// The same model as the website's proof scenes (sl-website2 app-scene
// LoopTimeline), played through the app's animation controller instead of
// generated CSS.

/** A stop: one time or a held range (ms of the loop) and its properties. */
export type SceneStop = readonly [
  at: number | readonly [number, number],
  frame: Keyframe,
];

/** One part of the scene (`data-scene-part`) and its frames over the loop. */
export interface SceneTrack {
  part: string;
  keyframes: Keyframe[];
  /** Own period for a part that repeats inside the loop (a caret blink). */
  durationMs?: number;
}

export interface SceneStory {
  loopMs: number;
  tracks: readonly SceneTrack[];
}

/** The scenes' movement curve between two stops. */
export const SCENE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * The stops as Web Animations keyframes over a loop of `loopMs`: a held range
 * becomes two frames, the first frame holds from 0 and the last until the end,
 * so the loop starts where it ends when both stops match.
 */
export function loopKeyframes(
  loopMs: number,
  stops: readonly SceneStop[]
): Keyframe[] {
  const frames = stops.flatMap(([at, frame]) =>
    (typeof at === 'number' ? [at] : [...at]).map(ms => {
      if (ms < 0 || ms > loopMs) {
        throw new Error(`Scene stop ${ms} ms is outside the ${loopMs} ms loop`);
      }
      return { easing: SCENE_EASING, ...frame, offset: ms / loopMs };
    })
  );
  if (frames.length === 0) throw new Error('A scene track needs a stop');
  frames.forEach((frame, index) => {
    if (index > 0 && frame.offset < frames[index - 1].offset) {
      throw new Error('Scene stops must not go back in time');
    }
  });
  if (frames[0].offset > 0) frames.unshift({ ...frames[0], offset: 0 });
  const last = frames[frames.length - 1];
  if (last.offset < 1) frames.push({ ...last, offset: 1 });
  return frames;
}

/** A pointer move: arrives at `arrive`, rests until `leave` at x/y. */
export type PointerMove = readonly [
  arrive: number,
  leave: number,
  x: number,
  y: number,
];

/**
 * A layer as large as the stage moved to design coordinates: translate
 * percentages of the layer are percentages of the stage.
 */
export function stageTranslate(
  x: number,
  y: number,
  size: { width: number; height: number }
): Keyframe {
  const percent = (value: number, of: number) =>
    `${Number(((value / of) * 100).toFixed(3))}%`;
  return {
    transform: `translate(${percent(x, size.width)}, ${percent(y, size.height)})`,
  };
}

/** The pointer layer's track: travelling between the rests of its moves. */
export function pointerTrack(
  loopMs: number,
  moves: readonly PointerMove[],
  size: { width: number; height: number }
): SceneTrack {
  return {
    part: 'pointer',
    keyframes: loopKeyframes(
      loopMs,
      moves.map(
        ([arrive, leave, x, y]) =>
          [[arrive, leave], stageTranslate(x, y, size)] as const
      )
    ),
  };
}

/**
 * Presses of the pointer: a click squeezes it for 180 ms, a held press (a
 * drag) from its start to its end.
 */
export function pressTrack(
  loopMs: number,
  presses: readonly (number | readonly [number, number])[]
): SceneTrack {
  const up: Keyframe = { transform: 'scale(1)' };
  const down: Keyframe = { transform: 'scale(0.84)' };
  const stops: SceneStop[] = [[0, up]];
  for (const press of presses) {
    const [from, to] = typeof press === 'number' ? [press, press] : press;
    stops.push([from - 90, up], [[from, to], down], [to + 90, up]);
  }
  stops.push([loopMs, up]);
  return { part: 'press', keyframes: loopKeyframes(loopMs, stops) };
}

/** The ring a click leaves around the pointer's tip. */
export function clickRingTrack(
  loopMs: number,
  clicks: readonly number[]
): SceneTrack {
  const rest: Keyframe = { opacity: 0, transform: 'scale(0.4)' };
  const stops: SceneStop[] = [[0, rest]];
  for (const click of clicks) {
    stops.push(
      [click - 1, rest],
      [click, { opacity: 0.55, transform: 'scale(0.4)', easing: 'ease-out' }],
      [click + 360, { opacity: 0, transform: 'scale(1.6)' }],
      [click + 361, rest]
    );
  }
  stops.push([loopMs, rest]);
  return { part: 'ring', keyframes: loopKeyframes(loopMs, stops) };
}

/** A part shown from `from` to `to`, fading over `edge` ms each way. */
export function shownTrack(
  part: string,
  loopMs: number,
  from: number,
  to: number,
  edge = 120,
  shown: Keyframe = {},
  hidden: Keyframe = {}
): SceneTrack {
  const off: Keyframe = { opacity: 0, ...hidden };
  const on: Keyframe = { opacity: 1, ...shown };
  return {
    part,
    keyframes: loopKeyframes(loopMs, [
      [[0, from], off],
      [[from + edge, to], on],
      [Math.min(loopMs, to + edge), off],
    ]),
  };
}

/** The springy curve of a part popping in. */
export const SCENE_POP_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/**
 * A part popping in at `at` and fading out from `out`; `base` is its own
 * transform (a tilt) kept in every frame.
 */
export function popTrack(
  part: string,
  loopMs: number,
  at: number,
  out: number,
  base = ''
): SceneTrack {
  const transform = (scale: number) => `${base} scale(${scale})`.trim();
  return {
    part,
    keyframes: loopKeyframes(loopMs, [
      [
        [0, at],
        { opacity: 0, transform: transform(0.88), easing: SCENE_POP_EASING },
      ],
      [[at + 320, out], { opacity: 1, transform: transform(1) }],
      [Math.min(loopMs, out + 300), { opacity: 0, transform: transform(1) }],
    ]),
  };
}

/** A part growing out of its transform origin from `at` to `at + 400`. */
export function growTrack(
  part: string,
  loopMs: number,
  at: number,
  out: number,
  axis: 'X' | 'Y'
): SceneTrack {
  const scale = (value: number) => `scale${axis}(${value})`;
  return {
    part,
    keyframes: loopKeyframes(loopMs, [
      [[0, at], { opacity: 1, transform: scale(0) }],
      [[at + 400, out], { opacity: 1, transform: scale(1) }],
      [out + 300, { opacity: 0, transform: scale(1) }],
      [out + 320, { opacity: 0, transform: scale(0) }],
      [out + 340, { opacity: 1, transform: scale(0) }],
    ]),
  };
}

/**
 * A connector (an SVG path with pathLength 1 and dash 1) drawn from `at` to
 * `at + 500`, fading out from `out`.
 */
export function drawTrack(
  part: string,
  loopMs: number,
  at: number,
  out: number
): SceneTrack {
  return {
    part,
    keyframes: loopKeyframes(loopMs, [
      // Hidden while undrawn: a round cap would leave a dot.
      [[0, at], { strokeDashoffset: 1, opacity: 0 }],
      [at + 60, { strokeDashoffset: 0.88, opacity: 1 }],
      [[at + 500, out], { strokeDashoffset: 0, opacity: 1 }],
      [out + 300, { strokeDashoffset: 0, opacity: 0 }],
      [out + 320, { strokeDashoffset: 1, opacity: 0 }],
    ]),
  };
}
